import { useEffect, useRef, useState } from "react";
import { ICustomerAddress } from "../models/ICustomerAddress";
import {
  getCustomerAddressList,
  updateCustomerAddress,
} from "../services/customerAddress";
import { getAddresses, getUserToken, saveAddresses } from "../utils/tokenUtils";
import axios from "axios";
import { useLanguage } from "../context/LanguageContext";

interface Props {
  onSuccess: () => void;
  onOpenNewAddress: () => void;
}

function DefaultAdressSelectPopup({ onSuccess, onOpenNewAddress }: Props) {
  const { t } = useLanguage();
  const [addresses, setAddresses] = useState<ICustomerAddress[]>([]);
  const [selectedId, setSelectedId] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const closeRef = useRef<HTMLButtonElement>(null);

  const loadAddresses = () => {
    setSelectedId("");
    setSaving(false);
    setDone(false);

    const cached = getAddresses();
    if (cached && cached.length > 0) {
      setAddresses(cached);
    } else {
      const token = getUserToken();
      if (token) {
        getCustomerAddressList(token.userId)
          .then((res) => {
            setAddresses(res.data);
            saveAddresses(res.data);
          })
          .catch((err) => {
            if (axios.isAxiosError(err) && err.response?.status === 404) {
              setAddresses([]);
            }
          });
      }
    }
  };

  useEffect(() => {
    const modalEl = document.getElementById("defaultAddressSelectModal");
    if (modalEl) {
      modalEl.addEventListener("show.bs.modal", loadAddresses);
      return () => modalEl.removeEventListener("show.bs.modal", loadAddresses);
    }
  }, []);

  const handleSetDefault = async () => {
    if (!selectedId) return;
    const addr = addresses.find((a) => a.id === selectedId);
    if (!addr) return;

    setSaving(true);
    try {
      await updateCustomerAddress(selectedId, {
        customerAddressDescription: addr.customerAddressDescription,
        customerAddressNote: addr.customerAddressNote,
        customerAddressIsDefault: true,
        customerAddressLatitude: addr.customerAddressLatitude,
        customerAddressLongitude: addr.customerAddressLongitude,
        cityId: addr.cityId,
        townId: addr.townId,
      });

      const token = getUserToken();
      if (token) {
        const res = await getCustomerAddressList(token.userId);
        saveAddresses(res.data);
      }

      setDone(true);
      setTimeout(() => {
        closeRef.current?.click();
        onSuccess();
      }, 2000);
    } catch {
      setSaving(false);
    }
  };

  return (
    <div
      className="modal fade"
      id="defaultAddressSelectModal"
      tabIndex={-1}
      aria-hidden="true"
      data-bs-backdrop="static"
      data-bs-keyboard="false"
    >
      <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
        <div className="modal-content rounded-4 shadow">
          <div className="modal-header background-red">
            <h5 className="modal-title text-white">
              <i className="bi bi-geo-alt-fill me-2"></i>{t("addressSelect.title")}
            </h5>
            <button
              ref={closeRef}
              type="button"
              className="btn-close btn-close-white"
              data-bs-dismiss="modal"
              aria-label={t("common.close")}
            />
          </div>

          <div className="modal-body">
            {done ? (
              <div className="text-center py-4">
                <i
                  className="bi bi-check-circle-fill text-success"
                  style={{ fontSize: "3rem" }}
                ></i>
                <p className="mt-3 fw-semibold">{t("addressSelect.updated")}</p>
              </div>
            ) : (
              <>
                <p className="text-muted small mb-3">
                  <i className="bi bi-info-circle me-1"></i>
                  {t("addressSelect.hint")}
                </p>
                <div className="d-flex flex-column gap-2">
                  {addresses.map((addr) => (
                    <div
                      key={addr.id}
                      className={`default-addr-item${selectedId === addr.id ? " default-addr-item--selected" : ""}`}
                      onClick={() => setSelectedId(addr.id)}
                    >
                      <i className="bi bi-geo-alt-fill default-addr-item__icon"></i>
                      <div className="flex-grow-1">
                        <div className="fw-semibold small">
                          {addr.city.cityNameTr} / {addr.town.townNameTr}
                        </div>
                        {addr.customerAddressDescription && (
                          <div style={{ fontSize: "0.78rem" }} className="text-muted">
                            {addr.customerAddressDescription}
                          </div>
                        )}
                      </div>
                      <i
                        className={`bi ${
                          selectedId === addr.id
                            ? "bi-check-circle-fill text-success"
                            : "bi-circle text-muted"
                        }`}
                      ></i>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          {!done && (
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                data-bs-dismiss="modal"
              >
                {t("common.close")}
              </button>
              <button
                type="button"
                className="btn btn-outline-danger btn-sm"
                data-bs-dismiss="modal"
                onClick={onOpenNewAddress}
              >
                <i className="bi bi-plus-circle me-1"></i>{t("addressSelect.addNew")}
              </button>
              <button
                type="button"
                className="btn btn-danger btn-sm px-4"
                disabled={!selectedId || saving}
                onClick={handleSetDefault}
              >
                {saving && (
                  <span className="spinner-border spinner-border-sm me-1"></span>
                )}
                {t("addressSelect.select")}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default DefaultAdressSelectPopup;
